/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef } from 'react';
import { View, Text, ActivityIndicator } from 'react-native';
import { App } from '@oolio-group/domain';
import { useTranslation } from '@oolio-group/localization';
import { useNavigation } from '@react-navigation/native';
import jwt_decode from 'jwt-decode';
import QrCode from 'react-qr-code';
import { useDeviceCodeLogin } from '@oolio-group/hooks';
import { SubNavigatorName } from '../../hooks/login/useInitialScreen';
import config from '../../config';
import { Session } from '../../state/Session';
import { setSession, setDeviceSelectionStep } from '../../state/preferences';
import { sessionSubject } from '../../state/sessionObservable';
import styles from './LoginScreen.styles';
import Gradient from '../../component/Gradient/Gradient';
import ButtonIcon from '../../component/Buttons/ButtonIcon';
import { useOrganization } from '../../hooks/organization/useOrganization';

const deviceCodeConfig = {
  clientId: config.auth0.clientId,
  audience: config.auth0.additionalParameters.audience,
  getTokenUrl: `${config.auth0.issuer}/oauth/token`,
  getDeviceCodeUrl: `${config.auth0.issuer}/oauth/device/code`,
};

const LoginScreen: React.FC = () => {
  const refreshQrCodeIntervalRef = useRef<any>();

  const { translate } = useTranslation();
  const navigation = useNavigation<any>();
  const {
    getDeviceCode,
    verificationUri,
    tokenPayload,
    userCode,
    expiresInRef: qrCodeTimeoutRef,
  } = useDeviceCodeLogin(deviceCodeConfig);

  // Tracks which tokenPayload we've already built a session for. This screen
  // stays mounted after navigating to AssignPos (React Navigation keeps prior
  // stack screens alive), so without this guard, organization?.country or
  // organization?.currencyCode resolving later re-fires the effect below and
  // rebuilds/overwrites the whole session from the original JWT claims —
  // wiping out the organization the user has since picked in AssignPos.
  const handledTokenPayloadRef = useRef<typeof tokenPayload | undefined>(
    undefined,
  );

  useEffect(() => {
    getDeviceCode();
  }, [getDeviceCode]);

  const session = sessionSubject.value;

  const { organization } = useOrganization(
    session.currentOrganization?.id || '',
  );

  useEffect(() => {
    // refresh the QR code when it expires
    if (userCode && qrCodeTimeoutRef.current)
      refreshQrCodeIntervalRef.current = setTimeout(
        getDeviceCode,
        qrCodeTimeoutRef.current,
      );
    return () => {
      clearTimeout(refreshQrCodeIntervalRef.current);
    };
  }, [getDeviceCode, qrCodeTimeoutRef, userCode]);

  useEffect(() => {
    if (tokenPayload && handledTokenPayloadRef.current !== tokenPayload) {
      handledTokenPayloadRef.current = tokenPayload;
      const idTokenPayload = jwt_decode<{
        app_metadata: any;
      }>(tokenPayload.idToken as string);
      const accesstokenPayload = jwt_decode<{
        exp: number;
      }>(tokenPayload.accessToken as string);
      const session: Session = {
        authorized: true,
        activeApp: App.CUSTOMER_DISPLAY_APP,
        token: tokenPayload.accessToken,
        refreshToken: tokenPayload.refreshToken,
        expiredDate: accesstokenPayload.exp * 1000,
        currentOrganization: {
          id: idTokenPayload.app_metadata.organization_id,
          country: organization?.country,
          currencyCode: organization?.currencyCode,
        },
      };
      setSession(session);
      sessionSubject.next(session);
      // Reset device selection step right after login and before navigating
      setDeviceSelectionStep(undefined);
      navigation.navigate(SubNavigatorName.AssignPos);
    }
  }, [
    tokenPayload,
    navigation,
    organization?.country,
    organization?.currencyCode,
  ]);

  return (
    <Gradient containerStyle={styles.screenContainer}>
      <View style={styles.screen} ph-label="login-screen">
        <View style={styles.modalContainer}>
          <Text style={styles.title}>
            {translate('deviceCodeLogin.titleCDS')}
          </Text>
          {!!verificationUri ? (
            <>
              <View style={styles.qrContainer}>
                <QrCode value={verificationUri} size={160} />
                <ButtonIcon
                  size={44}
                  icon="UilSync"
                  type="neutralLight"
                  onPress={getDeviceCode}
                  containerStyle={styles.btnRefresh}
                />
              </View>
              <View style={styles.codeContainer}>
                <Text style={styles.code}>{userCode}</Text>
              </View>
            </>
          ) : (
            <View style={styles.qrContainer}>
              <ActivityIndicator size={20} />
            </View>
          )}
          <Text style={styles.footnote}>
            {translate('deviceCodeLogin.description')}
          </Text>
        </View>
      </View>
    </Gradient>
  );
};

export default LoginScreen;
